import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Stack from 'components/core/Stack';
import Typography from 'components/core/Typography';
import Sheet from 'components/core/Sheet';
import Card from 'components/core/Card';
import { CircularProgress } from '@mui/joy';

import ObservationForm from 'components/ObservationForm';
import lookupTableActions from 'actions/lookupTableActions';
import observationActions from 'actions/observationActions';
import personActions from 'actions/personActions';
import noteActions from 'actions/noteActions';
import evidenceActions from 'actions/evidenceActions';
import Observation from 'domain/Observation';

const ObservationEdit = () => {
    const { observationId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [observation, setObservation] = useState(null);
    const [notes, setNotes] = useState([]);
    const [evidence, setEvidence] = useState([]);
    const [errors] = useState({});
    const personList = useSelector(({ person }) => person.list);

    useEffect(() => {
        fetchObservationData();
        fetchNotes();
        fetchEvidence();
        dispatch(lookupTableActions.fetchAllOptions());
        dispatch(personActions.list());
    }, [observationId]);

    const fetchObservationData = async () => {
        setLoading(true);
        setError(null);
        try {
            const query = new Parse.Query('Observation');
            query.include(['name', 'substantiation', 'location', 'location.parent', 'location.parent.parent', 
                           'town', 'locDeterminization', 'substrate', 'quality', 'event', 'keyword', 'observedBy', 'reviewedBy']);
            
            const observation = await query.get(observationId);
            setObservation(observation);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchNotes = async () => {
        try {
            const result = await dispatch(noteActions.list({ observation: observationId }));
            const existingNotes = (result.value || []).map(note => ({
                id: note.id,
                type: note.get('type'),
                text: note.get('text') || '',
                isDefault: true
            }));
            setNotes(existingNotes);
        } catch (err) {
        }
    };

    const fetchEvidence = async () => {
        try {
            const observationPointer = { 
                __type: 'Pointer', 
                className: 'Observation', 
                objectId: observationId 
            };
            
            const query = new Parse.Query('ObservationEvidence');
            query.equalTo('observation', observationPointer);
            query.include(['evidence', 'evidence.evidenceFormat', 'evidence.repository', 'evidence.sourceOwned']);
            const observationEvidence = await query.find();
            
            const evidenceList = observationEvidence.map(oe => oe.get('evidence'));
            setEvidence(evidenceList);
        } catch (err) {
            setEvidence([]);
        }
    };

    const handleSubmit = async (submittedObservation, submittedNotes, noteDiff, submittedEvidence, evidenceDiff) => {
        try {
            await submittedObservation.inspect();
            const observationToSave = new Observation();
            
            if (observation?.id) {
                observationToSave.id = observation.id;
            }
            
            observationToSave.set('name', submittedObservation.get('name')?.id);
            observationToSave.set('substantiation', submittedObservation.get('substantiation')?.id);
            observationToSave.set('locDeterminization', submittedObservation.get('locDeterminization')?.id);
            observationToSave.set('substrate', submittedObservation.get('substrate')?.id);
            observationToSave.set('quality', submittedObservation.get('quality')?.id);
            observationToSave.set('location', submittedObservation.get('location')?.id);
            observationToSave.set('town', submittedObservation.get('town')?.id);
            observationToSave.set('flag', submittedObservation.get('flag'));
            observationToSave.set('observationRestriction', submittedObservation.get('observationRestriction'));
            
            const lat = submittedObservation.get('lat');
            const lng = submittedObservation.get('lng');
            observationToSave.set('lat', lat ? parseFloat(lat) : null);
            observationToSave.set('lng', lng ? parseFloat(lng) : null);
            observationToSave.set('date', submittedObservation.get('date'));
            observationToSave.set('isDetected', submittedObservation.get('isDetected'));
            observationToSave.set('event', submittedObservation.get('event')?.id);
            observationToSave.set('keyword', submittedObservation.get('keyword')?.id);
            observationToSave.set('dateSpecificity', submittedObservation.get('dateSpecificity'));

            observationToSave.set('observedBy', submittedObservation.get('observedBy')?.get('person')?.id);
            observationToSave.set('reviewedBy', submittedObservation.get('reviewedBy')?.get('person')?.id);
            
            const saveObservationResult = await dispatch(observationActions.save(observationToSave));
            const savedObservation = saveObservationResult.value;
            
            const { toCreate, toUpdate, toDelete } = noteDiff;
            
            await Promise.all(toDelete.map(note => 
                dispatch(noteActions.remove(note.id))
            ));
            
            await Promise.all(toCreate.map(note => 
                dispatch(noteActions.save({
                    observation: savedObservation.id,
                    type: note.type.id,
                    text: note.text,
                    parent: savedObservation.id
                }))
            ));
            
            await Promise.all(toUpdate.map(note => 
                dispatch(noteActions.update(note.id, {
                    observation: savedObservation.id,
                    type: note.type.id,
                    text: note.text,
                    parent: savedObservation.id
                }))
            ));
            
            if (evidenceDiff) {
                const { toAttach, toDetach } = evidenceDiff;
                
                if (toDetach && toDetach.length > 0) {
                    await Promise.all(toDetach.map(evidence => 
                        dispatch(evidenceActions.detachFromObservation(evidence.id, savedObservation.id))
                    ));
                }
                
                if (toAttach && toAttach.length > 0) {
                    await Promise.all(toAttach.map(evidence => 
                        dispatch(evidenceActions.attachToObservation(evidence.id, savedObservation.id))
                    ));
                }
            }
            
            dispatch(observationActions.clearCurrent());
            navigate(`/observation/${savedObservation.id}`);
        } catch (err) {
        }
    };

    if (loading) {
        return (
            <Stack sx={{ alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <CircularProgress />
            </Stack>
        );
    }

    if (error) {
        return (
            <Stack sx={{ alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <Typography level="h4" color="danger">Error loading observation</Typography>
                <Typography>{error}</Typography>
            </Stack>
        );
    }

    const getPersonNameForPerson = (personObj) => {
        if (!personObj) {
            return null;
        }
        if (personObj.className === 'PersonName') {
            return personObj;
        }
        if (personObj.className !== 'Person') {
            return null;
        }
        
        if (!personList?.length) {
            const tempPersonName = new Parse.Object('PersonName');
            tempPersonName.id = `temp_${personObj.id}`;
            tempPersonName.set('person', personObj);
            tempPersonName.set('name', `${personObj.get('firstName')} ${personObj.get('lastName')}`);
            return tempPersonName;
        }
        
        const matchingPersonNames = personList.filter(personName => {
            const person = personName.get('person');
            return person && person.id === personObj.id;
        });
        
        if (matchingPersonNames.length === 0) {
            const tempPersonName = new Parse.Object('PersonName');
            tempPersonName.id = `temp_${personObj.id}`;
            tempPersonName.set('person', personObj);
            tempPersonName.set('name', `${personObj.get('firstName')} ${personObj.get('lastName')}`);
            return tempPersonName;
        }
        if (matchingPersonNames.length === 1) return matchingPersonNames[0];
        
        const officialName = `${personObj.get('firstName')} ${personObj.get('lastName')}`;
        const exactMatch = matchingPersonNames.find(personName => 
            personName.get('name') === officialName
        );
        
        return exactMatch || matchingPersonNames[0];
    };

    const observationForForm = observation ? new Observation(observation) : null;
    if (observationForForm) {
        observationForForm.observedBy = getPersonNameForPerson(observation.get('observedBy'));
        observationForForm.reviewedBy = getPersonNameForPerson(observation.get('reviewedBy'));
    }

    return (
        <Sheet sx={{ p: 4 }}>
            <Card>
                <Stack spacing={4}>
                    <Typography variant="h4">Edit Observation</Typography>
                    <ObservationForm
                        observation={observationForForm}
                        notes={notes}
                        evidence={evidence}
                        onSubmit={handleSubmit}
                        errors={errors}
                    />
                </Stack>
            </Card>
        </Sheet>
    );
};

export default ObservationEdit;